// Shared reconciliation for builder-resolved pricing rows.
//
// Pricing review metadata is mirrored at three levels: the constituent row,
// its cost breakdown, and the parent scope item. A builder edit only becomes a
// clean authority when all three agree. Keep this deterministic and I/O-free so
// both the Internal Pricing editor and Stage 4 inline Price Working use the
// same transition.

const COMMERCIAL_CATEGORIES = [
    'materials', 'labour', 'equipment', 'subcontractors', 'otherCosts', 'other',
];

const PENDING_RATE_SOURCES = new Set(['ai', 'ambiguous', 'unresolved']);
const PENDING_STATUSES = new Set(['unresolved', 'provisional']);
const CURRENT_UNRESOLVED_ROW_FIELDS = [
    'unresolvedReason', 'unresolved_reason',
    'candidates', 'candidateIds', 'candidate_ids',
    'supplierSelectionRequired', 'supplier_selection_required',
    'invalidPricingItemId', 'invalid_pricing_item_id',
    '_bookAmbiguous',
];

const text = value => String(value ?? '').trim();
const normalise = value => text(value).toLowerCase().replace(/\s+/g, ' ');
const normaliseCategory = value => normalise(value) === 'other' ? 'othercosts' : normalise(value);

function lineLabel(row, category) {
    if (normaliseCategory(category) === 'labour' || normaliseCategory(category) === 'subcontractors') {
        return text(row?.trade || row?.description || row?.name || 'Untitled line');
    }
    return text(row?.description || row?.name || row?.trade || 'Untitled line');
}

function candidateIds(value) {
    const direct = Array.isArray(value?.candidateIds)
        ? value.candidateIds
        : (Array.isArray(value?.candidate_ids) ? value.candidate_ids : []);
    const nested = Array.isArray(value?.candidates)
        ? value.candidates.map(candidate => candidate?.pricingItemId || candidate?.pricing_item_id || candidate?.id)
        : [];
    return [...new Set([...direct, ...nested].map(text).filter(Boolean))].sort();
}

export function pricingRowHasPendingResolution(row) {
    if (!row || typeof row !== 'object') return false;
    const statuses = [row.pricingStatus, row.pricing_status].map(normalise);
    const sources = [row._rateSource, row._rate_source].map(normalise);
    return statuses.some(status => PENDING_STATUSES.has(status))
        || sources.some(source => PENDING_RATE_SOURCES.has(source))
        || text(row.unresolvedReason).length > 0
        || text(row.unresolved_reason).length > 0
        || row.supplierSelectionRequired === true
        || row.supplier_selection_required === true
        || row._bookAmbiguous === true;
}

// What each input-rejected reason is waiting for, for the Price Working tag. A quantity-only rejection is the one
// pending state in which a retained rate-book match is still true: the row is held back by the input, not the rate.
const INPUT_NEEDED_BY_REASON = Object.freeze({
    invalid_material_quantity: 'a quantity',
    invalid_equipment_quantity: 'a quantity',
    invalid_labour_quantity: 'a quantity',
    invalid_subcontractor_quantity: 'a quantity',
    invalid_labour_duration: 'days',
    invalid_equipment_duration: 'days',
    invalid_subcontractor_duration: 'days',
    invalid_labour_hours: 'hours',
    invalid_labour_workers: 'a crew size',
    invalid_subcontractor_workers: 'a crew size',
});

// Truthy in any spelling is enough. These flags are written by the producer as an id string or `true`, and the
// failure direction here is deliberate: a flag misread as set leaves a rate decision visible, a flag misread as
// clear would hide one.
const flagIsSet = value => value === true || (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && value !== 0);

/**
 * Does the row say, in ANY recorded form, that its rate-book choice was rejected or is still a choice?
 * Such a row can carry leftover match-time metadata (`requestedPricingItemId`, `matchedName`, `matchedRate` are kept
 * as review provenance), but the explicit state wins: the match is not usable, so no "found" claim and no settled rate.
 * Forms: the camel and snake flags, the confidence / source / status spellings of "ambiguous", more than one
 * candidate, and any reason that is not a plain missing-input reason.
 */
function rowDeniesRateBookMatch(row) {
    if (flagIsSet(row.invalidPricingItemId) || flagIsSet(row.invalid_pricing_item_id)) return true;
    if (flagIsSet(row._bookAmbiguous) || flagIsSet(row._book_ambiguous)) return true;
    if (flagIsSet(row.supplierSelectionRequired) || flagIsSet(row.supplier_selection_required)) return true;
    const spellings = [
        row.confidence, row.matchConfidence, row.match_confidence,
        row._rateSource, row._rate_source, row.pricingStatus, row.pricing_status,
    ].map(normalise);
    if (spellings.includes('ambiguous')) return true;
    if (Array.isArray(row.candidates) && row.candidates.length > 1) return true;
    if (Array.isArray(row.candidateIds) && row.candidateIds.length > 1) return true;
    if (Array.isArray(row.candidate_ids) && row.candidate_ids.length > 1) return true;
    const reason = normalise(row.unresolvedReason ?? row.unresolved_reason);
    return reason !== '' && !Object.prototype.hasOwnProperty.call(INPUT_NEEDED_BY_REASON, reason);
}

/**
 * The rate-book match a row ACTUALLY carries, or null.
 *
 * A reason code alone does not prove a match: strict authority raises `invalid_material_quantity` for a row it never
 * matched, and an older save may have lost the match-time stamps. A match is claimed only when the immutable identity
 * is present AND the producer's match-time metadata (`_unresolvedFields` in pricing-match-applier.js) says what was
 * found: a name or a positive rate. Everything else is "no match recorded". An explicit rejected / ambiguous state
 * (rowDeniesRateBookMatch) overrides retained metadata.
 */
export function pricingRowRateBookMatch(row) {
    if (!row || typeof row !== 'object') return null;
    if (rowDeniesRateBookMatch(row)) return null;
    const id = text(row.requestedPricingItemId)
        || text(row.requested_pricing_item_id)
        || text(row.pricingItemId)
        || text(row.pricing_item_id);
    if (!id) return null;
    const name = text(row.matchedName ?? row.matched_name);
    const unit = text(row.matchedUnit ?? row.matched_unit);
    const rate = Number(row.matchedRate ?? row.matched_rate);
    const hasRate = Number.isFinite(rate) && rate > 0;
    if (!name && !hasRate) return null;
    return { id, name, unit, rate: hasRate ? rate : null };
}

/**
 * The AI Review / estimate price-list row a line named and was refused (`non_authoritative_estimate`, written by
 * pricing-match-applier.js and francis-work-type-coverage.js), or null. The line stays unpriced until the builder
 * approves that row on the Pricing page (DEC-PE-012); a re-cost then resolves exactly this id.
 */
export function pricingRowEstimateNomination(row) {
    if (!row || typeof row !== 'object') return null;
    if (normalise(row.unresolvedReason ?? row.unresolved_reason) !== 'non_authoritative_estimate') return null;
    const id = text(row.requestedPricingItemId) || text(row.requested_pricing_item_id);
    if (!id) return null;
    return { id, name: text(row.matchedName ?? row.matched_name) };
}

/**
 * The tag Stage 4 Price Working shows beside a line that has no applied rate-book link.
 *   manual    a builder-overridden line
 *   estimate  the line named an AI Review / estimate row, which may not price a quote until the builder approves it
 *   found     a rate-book row WAS found and is held back only because an input is missing (quantity, days, hours or
 *             crew). Requires actual match metadata (pricingRowRateBookMatch); the reason code alone is not enough.
 *   ai        everything else: not matched to the current rate book
 * The `ai` tag and title are unchanged. A found line is not relabelled as priced: its rate is not applied.
 */
export function pricingRowUnlinkedBadge(row) {
    if (row?._rateSource === 'manual') return { kind: 'manual', tag: 'MAN', title: 'Builder-overridden line' };
    if (normalise(row?.unresolvedReason ?? row?.unresolved_reason) === 'non_authoritative_estimate') {
        const name = text(row?.matchedName ?? row?.matched_name);
        return {
            kind: 'estimate',
            tag: 'AI REVIEW',
            title: `Names ${name ? `the AI Review rate "${name}"` : 'an AI Review rate'}. Not priced until you approve that rate on the Price List.`,
        };
    }
    const needs = INPUT_NEEDED_BY_REASON[normalise(row?.unresolvedReason ?? row?.unresolved_reason)];
    const match = needs ? pricingRowRateBookMatch(row) : null;
    if (match) {
        const what = [match.name, match.rate != null ? `$${match.rate}` : '', match.unit].filter(Boolean).join(' · ');
        return {
            kind: 'found',
            tag: 'FOUND',
            title: `Rate-book item found${what ? ` (${what})` : ''}. Not applied yet: enter ${needs} first.`,
        };
    }
    return { kind: 'ai', tag: 'AI', title: 'Not matched to the current rate book' };
}

export function pricingResolutionIdentity(row, category, rowIndex = null) {
    if (!row || typeof row !== 'object') return null;
    return {
        category: normaliseCategory(category),
        label: normalise(lineLabel(row, category)),
        reason: normalise(text(row.unresolvedReason) || text(row.unresolved_reason)),
        requestedPricingItemId: normalise(
            text(row.requestedPricingItemId)
            || text(row.requested_pricing_item_id)
            || text(row.invalidPricingItemId)
            || text(row.invalid_pricing_item_id),
        ),
        candidateIds: candidateIds(row),
        rowIndex: Number.isInteger(rowIndex) ? rowIndex : null,
    };
}

function identityKey(value) {
    return JSON.stringify([
        value?.category || '',
        value?.label || '',
        value?.reason || '',
        value?.requestedPricingItemId || '',
        value?.candidateIds || [],
    ]);
}

function storedFindingIdentity(value) {
    if (!value || typeof value !== 'object') return null;
    return {
        category: normaliseCategory(value.category),
        label: normalise(value.label),
        reason: normalise(value.reason),
        requestedPricingItemId: normalise(
            text(value.requestedPricingItemId)
            || text(value.requested_pricing_item_id)
            || text(value.invalidPricingItemId)
            || text(value.invalid_pricing_item_id),
        ),
        candidateIds: candidateIds(value),
        rowIndex: null,
    };
}

function findingMatchesResolution(finding, resolution) {
    if (!finding || !resolution) return false;
    if (!finding.category || !finding.label) return false;
    if (finding.category !== resolution.category || finding.label !== resolution.label) return false;
    if (finding.reason && resolution.reason && finding.reason !== resolution.reason) return false;
    if (finding.requestedPricingItemId && resolution.requestedPricingItemId
        && finding.requestedPricingItemId !== resolution.requestedPricingItemId) return false;
    if (finding.candidateIds.length > 0 && resolution.candidateIds.length > 0
        && !finding.candidateIds.some(id => resolution.candidateIds.includes(id))) return false;
    return true;
}

export function clearCurrentPricingResolutionState(row) {
    if (!row || typeof row !== 'object') return row;
    const requestedPricingItemId = text(row.requestedPricingItemId)
        || text(row.requested_pricing_item_id)
        || text(row.invalidPricingItemId)
        || text(row.invalid_pricing_item_id);
    if (requestedPricingItemId) row.requestedPricingItemId = requestedPricingItemId;
    delete row.requested_pricing_item_id;
    for (const key of CURRENT_UNRESOLVED_ROW_FIELDS) delete row[key];
    if (PENDING_STATUSES.has(normalise(row.pricingStatus))) delete row.pricingStatus;
    if (PENDING_RATE_SOURCES.has(normalise(row._rateSource))) delete row._rateSource;
    delete row.pricing_status;
    delete row._rate_source;
    delete row.pricingReviewRequired;
    delete row.pricing_review_required;
    delete row.pricingReviewWarning;
    delete row.pricing_review_warning;
    return row;
}

function storedUnresolvedFindings(item, breakdown) {
    // The item and breakdown arrays normally mirror the same review ledger,
    // but older saves can contain a finding on only one side. Merge by maximum
    // multiplicity so mirrors are not doubled and same-looking source rows are
    // still retained independently.
    const sources = [
        item?.pricingUnresolvedLines,
        item?.pricing_unresolved_lines,
        breakdown?.pricingUnresolvedLines,
        breakdown?.pricing_unresolved_lines,
    ].filter(value => Array.isArray(value) && value.length > 0);
    return sources.reduce((merged, source) => mergeFindingLedgers(merged, source), []);
}

function pendingRowFindings(breakdown) {
    const findings = [];
    for (const category of COMMERCIAL_CATEGORIES) {
        const rows = Array.isArray(breakdown?.[category]) ? breakdown[category] : [];
        rows.forEach((row, rowIndex) => {
            if (!pricingRowHasPendingResolution(row)) return;
            const identity = pricingResolutionIdentity(row, category, rowIndex);
            const unresolvedReason = text(row.unresolvedReason) || text(row.unresolved_reason);
            const requestedPricingItemId = text(row.requestedPricingItemId)
                || text(row.requested_pricing_item_id);
            findings.push({
                category: category === 'other' ? 'otherCosts' : category,
                label: lineLabel(row, category),
                reason: unresolvedReason || 'pricing_review_required',
                ...(requestedPricingItemId
                    ? { requestedPricingItemId }
                    : {}),
                ...(identity.candidateIds.length > 0 ? { candidateIds: identity.candidateIds } : {}),
            });
        });
    }
    return findings;
}

function mergeFindingLedgers(stored, live) {
    const storedCounts = new Map();
    for (const finding of stored) {
        const key = identityKey(storedFindingIdentity(finding));
        storedCounts.set(key, (storedCounts.get(key) || 0) + 1);
    }
    const liveCounts = new Map();
    const additions = [];
    for (const finding of live) {
        const key = identityKey(storedFindingIdentity(finding));
        const count = (liveCounts.get(key) || 0) + 1;
        liveCounts.set(key, count);
        if (count > (storedCounts.get(key) || 0)) additions.push(finding);
    }
    return [...stored, ...additions];
}

export function scopeItemHasUnresolvedPricing(item, { ignoreAmountAuthority = false } = {}) {
    const breakdowns = [item?.costBreakdown, item?.cost_breakdown]
        .filter(value => value && typeof value === 'object');
    if (breakdowns.length === 0) breakdowns.push({});
    const amountAuthorities = [item?.amountAuthority, item?.amount_authority].map(normalise);
    const statuses = [item?.pricingStatus, item?.pricing_status].map(normalise);
    const breakdownStatuses = breakdowns.flatMap(
        breakdown => [breakdown?.pricingStatus, breakdown?.pricing_status].map(normalise),
    );
    return statuses.some(status => PENDING_STATUSES.has(status))
        || breakdownStatuses.some(status => PENDING_STATUSES.has(status))
        || (!ignoreAmountAuthority && amountAuthorities.some(authority => (
            PENDING_STATUSES.has(authority) || authority === 'provisional-builder-review'
        )))
        || item?.pricingReviewRequired === true
        || item?.pricing_review_required === true
        || breakdowns.some(breakdown => (
            breakdown?.pricingReviewRequired === true
            || breakdown?.pricing_review_required === true
            || storedUnresolvedFindings(item, breakdown).length > 0
            || pendingRowFindings(breakdown).length > 0
        ));
}

export function reconcileScopeItemPricingResolution(
    item,
    resolvedIdentities = [],
    { costSource = 'internal_pricing_edit', pricingFingerprint = null } = {},
) {
    if (!item || typeof item !== 'object') return item;
    const breakdown = item.costBreakdown || item.cost_breakdown;
    if (!breakdown || typeof breakdown !== 'object') return item;
    const resolutions = resolvedIdentities.filter(Boolean);
    if (resolutions.length === 0) return item;

    const availableResolutions = [...resolutions];
    const retainedStored = storedUnresolvedFindings(item, breakdown).filter((finding) => {
        const findingIdentity = storedFindingIdentity(finding);
        const matchIndex = availableResolutions.findIndex(
            resolution => findingMatchesResolution(findingIdentity, resolution),
        );
        if (matchIndex < 0) return true;
        availableResolutions.splice(matchIndex, 1);
        return false;
    });
    const remaining = mergeFindingLedgers(retainedStored, pendingRowFindings(breakdown));
    const nextBreakdown = { ...breakdown };
    const next = { ...item, costBreakdown: nextBreakdown };
    if (next.amountAuthority == null && next.amount_authority != null) {
        next.amountAuthority = next.amount_authority;
    }
    delete next.amount_authority;
    delete next.cost_breakdown;

    if (remaining.length > 0) {
        next.pricingStatus = 'unresolved';
        next.pricingUnresolvedLines = remaining;
        next.pricingReviewRequired = true;
        nextBreakdown.pricingStatus = 'unresolved';
        nextBreakdown.pricingUnresolvedLines = remaining;
        nextBreakdown.pricingReviewRequired = true;
        delete next.pricing_status;
        delete next.pricing_unresolved_lines;
        delete next.pricing_review_required;
        delete nextBreakdown.pricing_status;
        delete nextBreakdown.pricing_unresolved_lines;
        delete nextBreakdown.pricing_review_required;
        return next;
    }

    next.pricingStatus = 'resolved';
    next.pricingUnresolvedLines = [];
    next.pricingReviewRequired = false;
    delete next.pricingReviewWarning;
    delete next.pricing_status;
    delete next.pricing_unresolved_lines;
    delete next.pricing_review_required;
    delete next.pricing_review_warning;

    nextBreakdown.pricingStatus = 'resolved';
    nextBreakdown.pricingUnresolvedLines = [];
    nextBreakdown.pricingReviewRequired = false;
    nextBreakdown.costSource = costSource;
    if (!nextBreakdown.pricingFingerprint && nextBreakdown.pricing_fingerprint) {
        nextBreakdown.pricingFingerprint = nextBreakdown.pricing_fingerprint;
    }
    if (pricingFingerprint) nextBreakdown.pricingFingerprint = pricingFingerprint;
    if (/unresolved|provisional|pending/i.test(text(nextBreakdown._costSource))) {
        delete nextBreakdown._costSource;
    }
    delete nextBreakdown.pricingReviewWarning;
    delete nextBreakdown.pricing_status;
    delete nextBreakdown.pricing_unresolved_lines;
    delete nextBreakdown.pricing_review_required;
    delete nextBreakdown.pricing_review_warning;
    delete nextBreakdown.pricing_fingerprint;
    return next;
}
